import { useAssignPostsMutation } from '@frontend/app/hooks/useAssignPosts';
import { Group, Project } from '@frontend/applications/Social/Analytics/components/filters-form/types';
import PostInsightsCard from '@frontend/applications/Social/Analytics/components/post-insights-card';
import { PostInsights } from '@frontend/gql/social/graphql';
import { useMessagingContext } from '@frontend/hooks';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import { Button } from '@revfluence/fresh';
import * as React from 'react';

const { useState } = React;

interface IProps {
  post: PostInsights;
  projects: Project[];
  groups: Group[];
}

export const NewSocialPostAssignment: React.FunctionComponent<IProps> = ({ post, projects, groups }) => {
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');

  const [assignPosts] = useAssignPostsMutation();
  const { showSuccessMessage, showErrorMessage } = useMessagingContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const selectedProject = projects.find(
        (project) => project.id.toString() === selectedProjectId
      );
      const projectName = selectedProject?.name;

      const res = await assignPosts({
        variables: {
          mentionIds: [post.mentionId],
          programIds: selectedProjectId ? [parseInt(selectedProjectId, 10)] : [],
          communityIds: selectedGroupId ? [parseInt(selectedGroupId, 10)] : [],
          programNames: projectName ? [projectName] : [],
        },
      });
      if (res.errors) {
        showErrorMessage('Failed to assign post. Please try again.');
      }
      showSuccessMessage('Assigned post');
    } catch (err) {
      showErrorMessage('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-2">Assign your post</h1>
        <p className="text-base text-muted-foreground max-w-md mx-auto">
          Set assignments for your post. Associated content will share the same assignment as this post.
        </p>
      </div>

      {/* Form */}
      <div className="flex flex-col md:flex-row items-center justify-center gap-4">
        {/* Project Selector */}
        <Select onValueChange={setSelectedProjectId}>
          <SelectTrigger className="w-64" aria-label="Select Project">
            <SelectValue placeholder="Select Project" />
          </SelectTrigger>
          <SelectContent className="z-[1000]">
            {projects.map((project) => (
              <SelectItem key={project.id} value={project.id.toString()}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Group Selector */}
        <Select onValueChange={setSelectedGroupId}>
          <SelectTrigger className="w-64" aria-label="Select Group">
            <SelectValue placeholder="Select Group" />
          </SelectTrigger>
          <SelectContent className="z-[1000]">
            {groups.map((group) => (
              <SelectItem key={group.id} value={group.id.toString()}>
                {group.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          onClick={handleSubmit}
          disabled={(!selectedProjectId && !selectedGroupId) || isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </div>
      <div className="flex justify-center">
        <div className="w-1/2">
          <a
            href={post.postPermalink}
            target="_blank"
            rel="noopener noreferrer"
          >
            <PostInsightsCard
              post={post}
              isSelected={false}
              removeSelectionCheckbox
            />
          </a>
        </div>
      </div>
    </div>
  );
};
